import { Button, Modal, Select, Spin, message } from "antd";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { svg } from "../../../../Assets/svg";
import { useAppDispatch } from "../../../../store";
import { createOrderAPI, getApprovedQuotationsListAPI } from "../orderApis";
import { CapitalizeFirstWord } from "../../../../helpers/utils";
import ConfigureDrawingsModal from "../../Quotations/Components/ConfigureDrawingsModal";
import {
  ORDER_FILTER_KEY,
  ORDER_REFETCH_KEY,
  ORDER_SCROLL_KEY,
} from "../orderTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: any;
  onAddressIncompleteError: (data: any) => void;
  onCustomerOnHoldError: (data: any) => void;
}
const CreateOrderModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  onAddressIncompleteError,
  onCustomerOnHoldError,
}) => {
  const dispatch = useAppDispatch();

  // Form State
  const [submitting, setSubmitting] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [drawingRequiredData, setDrawingRequiredData] = useState<any>(null);

  // Pagination & Data State
  const [quoteOptions, setQuoteOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const limit = 20; // Items per page

  const fetchQuotations = (
    currentPage: number,
    search: string,
    isLoadMore: boolean,
  ) => {
    setLoadingOptions(true);

    dispatch(
      getApprovedQuotationsListAPI({
        filters: { page: currentPage, limit: limit },
        onSuccess: (response: any) => {
          const newOptions = response?.quotes?.map((q: any) => {
            const newLabel = `#${q.display_quote_id} - ${
              q?.customer_snapshot?.company_name || ""
            } - ${
              CapitalizeFirstWord(q?.customer_snapshot?.first_name) || ""
            } ${CapitalizeFirstWord(q?.customer_snapshot?.last_name) || ""}`;
            return {
              label: newLabel,
              value: q._id,
            };
          });
          if (isLoadMore) {
            setQuoteOptions((prev) => [...prev, ...newOptions]);
          } else {
            setQuoteOptions(newOptions);
          }

          // Check if we have more data to load
          if (newOptions.length < limit) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }

          setLoadingOptions(false);
        },
        onError: () => {
          message.error("Failed to fetch approved quotations");
          setLoadingOptions(false);
        },
      }),
    );
  };

  // ✅ 2. Initial Load & Reset on Open
  useEffect(() => {
    if (open) {
      setSelectedQuoteId(null);
      setPage(1);
      setSearchTerm("");
      setHasMore(true);
      fetchQuotations(1, "", false);
    }
  }, [open]);

  // ✅ 3. Handle Scroll (Load More)
  const handlePopupScroll = (e: any) => {
    const { target } = e;
    // Check if scrolled to bottom (with small buffer of 10px)
    if (
      target.scrollTop + target.offsetHeight + 10 >= target.scrollHeight &&
      !loadingOptions &&
      hasMore
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchQuotations(nextPage, searchTerm, true);
    }
  };

  // ✅ 4. Handle Search (Debounced)

  // ✅ 5. Handle Submit
  const handleSubmit = () => {
    if (!selectedQuoteId) return;
    setSubmitting(true);
    dispatch(
      createOrderAPI({
        payload: { quote_id: selectedQuoteId },
        onSuccess: (data) => {
          setSubmitting(false);
          onSuccess(data);
          onClose();
          sessionStorage.removeItem(ORDER_FILTER_KEY);
          sessionStorage.removeItem(ORDER_SCROLL_KEY);
          sessionStorage.setItem(ORDER_REFETCH_KEY, "1");
        },
        onError: () => {
          setSubmitting(false);
        },
        onAddressIncompleteError: (data: any) => {
          onAddressIncompleteError(data);
        },
        onDrawingRequiredError: (data: any) => {
          setDrawingRequiredData(data?.data || {});
        },
        onCustomerOnHoldError: (data: any) => {
         onCustomerOnHoldError(data);
        },
      }),
    );
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={svg?.closeIcon}
      width="33.75rem"
      className="commonModalClass"
      destroyOnClose={true}
      title={
        <div className="text-center fs-4 fw-bolder lh-m">Create Order</div>
      }
    >
      <div className="d-flex flex-column justify-content-center gap-4 py-3">
        <p className="text-center text-4a4a4a mb-0">
          This action will create a new order based on the selected Quotation
          details. Please review all information before proceeding.
        </p>

        <div className="page-form">
          <label className="fw-semibold mb-1 d-block">Quote Id</label>
          <Select
            className="w-100"
            placeholder="Select an approved quotation"
            size="large"
            value={selectedQuoteId}
            onChange={(value) => setSelectedQuoteId(value)}
            options={quoteOptions}
            // Search Configuration
            filterOption={false} // Disable client-side filtering since we do server-side
            // Pagination Configuration
            onPopupScroll={handlePopupScroll}
            notFoundContent={
              loadingOptions ? (
                <div className="text-center p-2">
                  <Spin size="small" />
                </div>
              ) : quoteOptions?.length == 0 && !loadingOptions ? (
                <div className="text-center p-2">
                  No approved quotations found
                </div>
              ) : (
                ""
              )
            }
            disabled={submitting}
            suffixIcon={
              loadingOptions ? <Spin size="small" /> : svg?.selectDownArrow
            }
            // Add a footer to show loading more spinner
            dropdownRender={(menu) => (
              <>
                {menu}
                {loadingOptions && page > 1 && (
                  <div className="text-center p-2 text-muted small">
                    <Spin size="small" className="me-2" /> Loading more...
                  </div>
                )}
              </>
            )}
          />
        </div>

        <div className="d-flex justify-content-center gap-3 mt-2">
          <Button
            onClick={onClose}
            className="btn btn-lg btn-outline-secondary h-auto px-5"
            disabled={submitting}
          >
            CLOSE
          </Button>

          <Button
            onClick={handleSubmit}
            className={`btn btn-lg btn-primary h-auto px-5`}
            loading={submitting}
            disabled={!selectedQuoteId}
          >
            SUBMIT
          </Button>
        </div>
      </div>

      {drawingRequiredData !== null && (
        <ConfigureDrawingsModal
          open={drawingRequiredData !== null}
          onClose={() => setDrawingRequiredData(null)}
          drawingsData={drawingRequiredData?.variants_requiring_drawing}
          partsData={drawingRequiredData?.parts_requiring_drawing}
          componentsData={drawingRequiredData?.components_requiring_drawing}
          fullDrawingData={drawingRequiredData}
          quotationId={drawingRequiredData?.quote_id}
          onSubmit={(payload, setModalLoader) => {
            setModalLoader(true);
            dispatch(
              createOrderAPI({
                payload,
                onSuccess: (data) => {
                  setModalLoader(false);
                  setDrawingRequiredData(null);
                  onSuccess(data);
                  onClose();
                  sessionStorage.removeItem(ORDER_FILTER_KEY);
                  sessionStorage.removeItem(ORDER_SCROLL_KEY);
                  sessionStorage.setItem(ORDER_REFETCH_KEY, "1");
                },
                onError: () => setModalLoader(false),
                onAddressIncompleteError: (data) => {
                  setModalLoader(false);
                  setDrawingRequiredData(null);
                  onAddressIncompleteError(data);
                },
                onDrawingRequiredError: (data) => {
                  setDrawingRequiredData(data?.data || {});
                },
              })
            );
          }}
        />
      )}
    </Modal>
  );
};

export default CreateOrderModal;
